import { GoogleGenerativeAI } from '@google/generative-ai';

// Initialize the Gemini API client
// We use import.meta.env for Vite environment variables
const API_KEY = import.meta.env.VITE_GEMINI_API_KEY;
let genAI = null;

if (API_KEY) {
  genAI = new GoogleGenerativeAI(API_KEY);
}

export const generateWorkoutAnalysis = async (workoutData, userProfile, pastWorkouts = []) => {
  if (!genAI) {
    console.warn("Gemini API Key is missing. Skipping AI analysis.");
    return null;
  }

  try {
    const model = genAI.getGenerativeModel({ model: "gemini-flash-lite-latest" });

    const feelingMap = {
      easy: 'Facile',
      normal: 'Normal',
      hard: 'Difficile',
      extreme: 'Extrême'
    };
    const translatedFeeling = feelingMap[workoutData.feeling] || workoutData.feeling || 'Non précisé';

    // Format the workout data into a readable string for the AI
    let workoutSummary = `Durée: ${workoutData.duration || '?'} min\n`;
    workoutSummary += `Repos moyen: ${workoutData.restTime || '?'} min\n`;
    workoutSummary += `Ressenti global: ${translatedFeeling}\n\nExercices réalisés:\n`;

    workoutData.exercises.forEach(block => {
      const items = block.type === 'superset' ? block.items : [block];
      items.forEach(ex => {
        if (!ex.sets) return;
        workoutSummary += `- ${ex.name}: `;
        const setsInfo = ex.sets.map(s => {
          if (s.isUnilateral) {
             return `${s.weightL || 0}kg x ${s.repsL || 0} / ${s.weightR || 0}kg x ${s.repsR || 0}`;
          }
          return `${s.weight || 0}kg x ${s.reps || 0}`;
        }).join(', ');
        workoutSummary += `${setsInfo}\n`;
      });
    });

    let historicalSummary = "";
    if (pastWorkouts && pastWorkouts.length > 0) {
      const recentWorkouts = pastWorkouts.slice(-3);
      historicalSummary = "\nHistorique récent (pour évaluer la progression):\n";
      recentWorkouts.forEach(w => {
        const dateStr = w.timestamp ? new Date(w.timestamp).toLocaleDateString() : w.date;
        historicalSummary += `- Date: ${dateStr}\n`;
        w.exercises.forEach(b => {
          const items = b.type === 'superset' ? b.items : [b];
          items.forEach(ex => {
            if(!ex.sets || ex.sets.length === 0) return;
            const maxWeight = Math.max(...ex.sets.map(s => s.isUnilateral ? Math.max(Number(s.weightL)||0, Number(s.weightR)||0) : (Number(s.weight)||0)));
            if (maxWeight > 0) historicalSummary += `  * ${ex.name}: max ${maxWeight}kg\n`;
          });
        });
      });
    }

    const systemPrompt = `Tu es un coach de musculation expert, professionnel, cool et très motivant. Tu as une expertise poussée en biomécanique, hypertrophie et périodisation, et tu communiques de manière claire, encourageante et moderne.
L'utilisateur s'appelle ${userProfile?.name || 'l\'athlète'}. Il vient de terminer sa séance de sport.
Voici les données de sa séance du jour :
${workoutSummary}
${historicalSummary}

Instructions d'analyse :
1. Fais un retour ultra-personnalisé, pro et motivant (3 à 4 phrases maximum). Va droit au but, pas de blabla.
2. NE TE BASE PLUS SUR DES RÈGLES BASIQUES (comme "6-8 reps = bien", "3 min = bien"). Fais appel à tes vraies connaissances.
   - 12-15 reps est excellent pour de l'isolation (élévations latérales, curl).
   - 1-2 min de repos est suffisant pour des petits muscles, mais 3-5 min est requis pour du squat lourd.
3. Apporte une VRAIE plus-value :
   - Synergie des exercices : a-t-il fait trop de poussée par rapport au tirage ? Donne un conseil pertinent pour équilibrer.
   - Surcharge progressive : remarque s'il a progressé, ou donne une piste concrète s'il a stagné.
4. Ne fais jamais la morale et ne sois pas sec. Sois positif, digne d'un coach de haut niveau qui tire son athlète vers le haut.
5. Parle en tutoyant, avec un ton naturel et respectueux.
6. Ne fais pas d'introduction générique. Donne directement ton retour expert.
7. Ne mets pas de texte en gras ou de markdown complexe. Tu peux utiliser 1 ou 2 emojis discrets pour aérer le texte.`;

    const result = await model.generateContent(systemPrompt);
    const response = await result.response;
    return response.text();
    
  } catch (error) {
    console.error("Erreur lors de la génération de l'analyse IA:", error);
    return null;
  }
};

export const getAIExerciseAlternatives = async (originalExercise, allExercises) => {
  if (!genAI || !originalExercise || !allExercises || allExercises.length === 0) {
    return [];
  }

  try {
    const model = genAI.getGenerativeModel({ model: "gemini-flash-lite-latest" });

    // Format the list of all available exercises
    const exercisesList = allExercises.map(ex => `- ID: ${ex.id} | Nom: ${ex.name} | Muscles: ${ex.muscles?.join(',') || ex.muscle || 'Autre'} | Équipement: ${ex.equipment || 'Autre'}`).join('\n');

    const systemPrompt = `Tu es un expert en biomécanique et en musculation.
L'utilisateur veut remplacer l'exercice suivant car il ne peut pas le faire (douleur ou machine occupée) :
Nom : ${originalExercise.name}
Muscles ciblés : ${originalExercise.muscles?.join(', ') || originalExercise.muscle || 'Autre'}
Équipement utilisé : ${originalExercise.equipment || 'Autre'}

Voici la liste de TOUS les exercices disponibles dans sa base de données :
${exercisesList}

Instructions :
1. Trouve 3 exercices de remplacement pertinents parmi cette liste exacte. Ils doivent cibler le même groupe musculaire principal et si possible proposer un schéma moteur similaire (ex: poussée horizontale, tirage vertical).
2. Ne propose QUE des exercices présents dans la liste fournie.
3. Renvoie ta réponse UNIQUEMENT sous la forme d'un tableau JSON contenant les IDs des 3 exercices, rien d'autre. Pas de blabla, pas de markdown \`\`\`json. Juste le tableau.
Exemple de réponse : ["id1", "id2", "id3"]`;

    const result = await model.generateContent(systemPrompt);
    const responseText = await result.response.text();
    
    // Parse the JSON array
    const cleanText = responseText.replace(/```json/g, '').replace(/```/g, '').trim();
    const suggestedIds = JSON.parse(cleanText);
    
    if (Array.isArray(suggestedIds)) {
      return suggestedIds;
    }
    return [];

  } catch (error) {
    console.error("Erreur lors de la récupération des alternatives IA:", error);
    return [];
  }
};

export const generateWeeklyAnalysis = async (volumeData, userProfile) => {
  if (!genAI || !volumeData || volumeData.length === 0) {
    return null;
  }

  try {
    const model = genAI.getGenerativeModel({ 
      model: "gemini-flash-lite-latest",
      generationConfig: { responseMimeType: "application/json" }
    });

    const volumeText = volumeData.map(v => `- ${v.muscle}: ${v.volume} kg`).join('\n');

    const systemPrompt = `Tu es un coach de musculation expert en data-analyse.
L'utilisateur s'appelle ${userProfile?.name || 'l\'athlète'}.

Voici le volume total soulevé (Tonnage) par groupe musculaire sur les 7 derniers jours :
${volumeText}

Instructions TRÈS IMPORTANTES d'analyse :
1. Analyse cette répartition pour identifier le point fort et le point faible de la semaine.
2. CLÉMENCE ABSOLUE : L'utilisateur utilise parfois de petites charges ou ne compte que le poids ajouté à la machine/barre. Ne considère JAMAIS qu'un tonnage est "trop faible". Ne dis JAMAIS que le haut du corps ou un autre muscle est un "point faible" à cause d'une charge légère. Le bas du corps (Quadriceps, Ischio-jambiers, Fessiers) soulève souvent beaucoup plus lourd que le reste, c'est normal.
3. LE VRAI POINT FAIBLE : Un "point faible" est UNIQUEMENT un gros groupe musculaire avec **0 série** ou **0 kg**. Si l'utilisateur a fait des séries pour les Pectoraux (sets > 0) ou n'importe quel autre gros muscle, ALORS CE N'EST PAS UN POINT FAIBLE, même si le volume est de 20 kg contre 5000 kg ailleurs. Si tous les gros muscles ont au moins 1 série, écris simplement : "Aucun point faible cette semaine, ton entraînement est parfaitement équilibré !" au lieu de chercher un défaut absolu. IGNORE COMPLÈTEMENT les petits muscles secondaires comme les Avant-bras ou les Mollets pour le point faible. Ne les cite jamais en point faible.
4. Propose un conseil clair pour la semaine prochaine basé uniquement sur les déséquilibres de fréquence ou l'oubli de certains muscles.
5. Rédige de manière bienveillante, motivante et en tutoyant.
6. Renvoie ta réponse STRICTEMENT sous la forme d'un objet JSON avec les clés suivantes :
   - "forts" : Une phrase très courte sur le point fort de la semaine (avec un emoji).
   - "faibles" : Une phrase très courte sur ce qui a été délaissé (avec un emoji).
   - "conseil" : Un conseil de 1 à 2 phrases pour la semaine prochaine.

Pas de markdown \`\`\`json, renvoie uniquement l'objet JSON valide.`;

    const result = await model.generateContent(systemPrompt);
    const responseText = await result.response.text();
    
    // Safety cleanup in case the model ignored responseMimeType
    const cleanText = responseText.replace(/```json/gi, '').replace(/```/g, '').trim();
    const analysis = JSON.parse(cleanText);
    
    return analysis;

  } catch (error) {
    console.error("Erreur lors de la génération du bilan hebdomadaire IA:", error);
    return null;
  }
};

export const generateSmartWorkout = async (timeMinutes, targetMuscles, availableExercises, equipmentContext = 'all') => {
  if (!genAI || !availableExercises || availableExercises.length === 0) {
    return null;
  }

  try {
    const model = genAI.getGenerativeModel({ 
      model: "gemini-flash-lite-latest",
      generationConfig: { responseMimeType: "application/json" }
    });

    const exercisesList = availableExercises.map(ex => `- ID: ${ex.id} | Nom: ${ex.name} | Muscles: ${ex.muscles?.join(',') || ex.muscle || 'Autre'} | Equip: ${ex.equipment || 'Autre'}`).join('\n');

    const maxSets = Math.floor(timeMinutes / 3.5); // 1 min exec + 2.5 min rest per set
    
    let homeContext = "";
    if (equipmentContext === 'home') {
      homeContext = "\n- CONTEXTE: Entraînement à la MAISON. Priorise les exercices avec HALTÈRES (s'ils existent dans la liste) par rapport au poids du corps.";
    }

    const systemPrompt = `Tu es un expert en programmation sportive.
L'utilisateur veut une séance de musculation sur-mesure.
- Temps max : ${timeMinutes} minutes. En comptant l'exécution et 2 à 3 minutes de repos, il peut faire MAXIMUM ${maxSets} séries (sets) AU TOTAL sur toute la séance.
- Muscles ciblés : ${targetMuscles.join(', ')}${homeContext}

Voici la liste de TOUS les exercices disponibles dans sa base de données :
${exercisesList}

Instructions :
1. Crée un programme cohérent qui ne dépasse pas ${maxSets} séries au total (tous exercices confondus). S'il a demandé 20 minutes, ça fait environ 5 séries max, donc 1 ou 2 exercices seulement ! Respecte ce temps.
2. Ne choisis QUE des exercices présents dans la liste (utilise l'ID exact).
3. Ne gère pas les répétitions ou charges. Donne SEULEMENT le nombre de séries (sets).
4. Donne un nom cool, fun et motivant à la séance dans "routineName" (ex: "Explosion des Pecs Express").
5. Renvoie UNIQUEMENT un objet JSON valide, pas un mot de plus, sans bloc markdown.

Format JSON attendu :
{
  "routineName": "Nom de la séance",
  "exercises": [
    { "exerciseId": "ID_EXACT", "sets": 3 }
  ]
}`;

    const result = await model.generateContent(systemPrompt);
    const responseText = await result.response.text();
    
    // Extract JSON safely using regex
    const jsonMatch = responseText.match(/\{[\s\S]*\}/);
    if (!jsonMatch) throw new Error("Format JSON introuvable");
    
    const cleanText = jsonMatch[0];
    return JSON.parse(cleanText);

  } catch (error) {
    console.error("Erreur lors de la génération de la séance IA:", error);
    return null;
  }
};
